import { forwardRef, type HTMLAttributes } from 'react';

interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding scale. Default: `md` (1.5rem). `none` skips padding. */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Border radius scale. Default: `xl` (12px) per design system. */
  radius?: 'md' | 'lg' | 'xl' | '2xl';
}

const PADDING: Record<NonNullable<GlassPanelProps['padding']>, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const RADIUS: Record<NonNullable<GlassPanelProps['radius']>, string> = {
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
};

/**
 * Glass-style translucent panel. The core visual primitive of the
 * EstateIntelligence design system — translucent white over the parchment
 * background, backdrop blur, hairline forest-green border, white specular
 * inset highlight on the top-left edge.
 *
 * Use for: KPI cards, dashboard panels, chart containers, premium content blocks.
 * Avoid for: dense data tables (use a flat card instead — backdrop-blur is
 * expensive on long lists) and inside modals (double-blur looks muddy).
 */
export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(
  ({ padding = 'md', radius = 'xl', className = '', children, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        className={`glass-panel ${RADIUS[radius]} ${PADDING[padding]} ${className}`}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

GlassPanel.displayName = 'GlassPanel';
